<template>
  <div class="invoice-filter-program-fields">
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{
        $t('invoice.academicStatus')
      }}</label>
      <a-select
        v-model="academicStatusId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :options="academicStatusOptions"
        showSearch
        :filterOption="filterSelectOption"
        :open="academicStatusOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onAcademicStatusChange"
        @dropdownVisibleChange="onAcademicStatusDropdownVisibleChange"
      ></a-select>
    </div>
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{
        $t('invoice.academicYear')
      }}</label>
      <a-select
        v-model="academicYearId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :loading="academicYearLoading"
        :options="academicYearOptions"
        showSearch
        :filterOption="filterSelectOption"
        :open="academicYearOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onAcademicYearChange"
        @dropdownVisibleChange="onAcademicYearDropdownVisibleChange"
      ></a-select>
      <p
        v-if="academicYearLoading"
        class="invoice-filter-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch spin" aria-hidden="true"></i>
        {{ $t('invoice.loadingOptions') }}
      </p>
    </div>
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{
        $t('invoice.mainProgram')
      }}</label>
      <a-select
        v-model="apcId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :loading="mainProgramLoading"
        :options="apcList"
        showSearch
        :filterOption="filterSelectOption"
        :open="mainProgramOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onMainChange"
        @dropdownVisibleChange="onMainDropdownVisibleChange"
      ></a-select>
      <p
        v-if="mainProgramLoading"
        class="invoice-filter-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch spin" aria-hidden="true"></i>
        {{ $t('invoice.loadingOptions') }}
      </p>
    </div>
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{ $t('invoice.subProgram') }}</label>
      <a-select
        v-model="apcsId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :loading="subProgramLoading"
        :options="subProgramOptions"
        showSearch
        :filterOption="filterSelectOption"
        :disabled="!hasMainProgram"
        :open="subProgramOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onSubProgramChange"
        @dropdownVisibleChange="onSubProgramDropdownVisibleChange"
      ></a-select>
      <p
        v-if="subProgramLoading"
        class="invoice-filter-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch spin" aria-hidden="true"></i>
        {{ $t('invoice.loadingOptions') }}
      </p>
    </div>
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{
        $t('invoice.academicTerm')
      }}</label>
      <a-select
        v-model="academicTermId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :loading="academicTermLoading"
        :options="academicTermOptions"
        showSearch
        :filterOption="filterSelectOption"
        :disabled="!hasBranch || !hasMainProgram || !hasAcademicYear"
        :open="academicTermOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onAcademicTermChange"
        @dropdownVisibleChange="onAcademicTermDropdownVisibleChange"
      ></a-select>
      <p
        v-if="academicTermLoading"
        class="invoice-filter-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch spin" aria-hidden="true"></i>
        {{ $t('invoice.loadingOptions') }}
      </p>
    </div>
    <div class="invoice-filter-field">
      <label class="invoice-filter-label">{{
        $t('invoice.classOperation')
      }}</label>
      <a-select
        v-model="classId"
        mode="multiple"
        :allowClear="true"
        :maxTagCount="1"
        :maxTagTextLength="24"
        :loading="classLoading"
        :options="classOperationOptions"
        showSearch
        :filterOption="filterSelectOption"
        :disabled="!hasBranch || !hasMainProgram || !hasAcademicYear"
        :open="classOperationOpen"
        :placeholder="$t('all')"
        class="ts-w-full"
        @change="onClassOperationChange"
        @dropdownVisibleChange="onClassOperationDropdownVisibleChange"
      ></a-select>
      <p
        v-if="classLoading"
        class="invoice-filter-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch spin" aria-hidden="true"></i>
        {{ $t('invoice.loadingOptions') }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'mainProgramSelectBox',
  props: {
    companyId: {
      type: Array,
      default: () => []
    },
    branchId: {
      type: Array,
      default: () => []
    },
    availableBranchIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      mainProgramLoading: false,
      subProgramLoading: false,
      academicYearLoading: false,
      academicTermLoading: false,
      classLoading: false,
      subProgramRequestId: 0,
      academicYearRequestId: 0,
      academicTermRequestId: 0,
      classOperationRequestId: 0,
      mainProgramOpen: false,
      subProgramOpen: false,
      academicStatusOpen: false,
      academicYearOpen: false,
      academicTermOpen: false,
      classOperationOpen: false,
      subProgramOptions: [],
      academicYearOptions: [],
      academicTermOptions: [],
      classOperationOptions: []
    }
  },
  async created() {
    this.mainProgramLoading = true
    await this.getMainProgram().finally(() => (this.mainProgramLoading = false))
    this.loadAcademicYears()
  },
  computed: {
    apcId: {
      get() {
        return this.$store.state.invoice.acp_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_APC_ID', newValue)
      }
    },
    apcsId: {
      get() {
        return this.$store.state.invoice.acps_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_APCS_ID', newValue)
      }
    },
    academicStatusId: {
      get() {
        return this.$store.state.invoice.ac_status_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_AC_STATUS_ID', newValue)
      }
    },
    academicYearId: {
      get() {
        return this.$store.state.invoice.ac_year_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_AC_YEAR_ID', newValue)
      }
    },
    academicTermId: {
      get() {
        return this.$store.state.invoice.ac_term_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_AC_TERM_ID', newValue)
      }
    },
    classId: {
      get() {
        return this.$store.state.invoice.class_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_CLASS_ID', newValue)
      }
    },
    effectiveBranchIds() {
      return this.branchId.length ? this.branchId : this.availableBranchIds
    },
    hasBranch() {
      return this.effectiveBranchIds.length > 0
    },
    hasMainProgram() {
      return this.apcId.length > 0
    },
    hasAcademicYear() {
      return this.academicYearId.length > 0
    },
    hasMultipleCompanies() {
      return new Set(this.companyId.map(id => String(id))).size > 1
    },
    academicStatusOptions() {
      return [
        { value: 1, label: this.$t('invoice.current') },
        { value: 3, label: this.$t('invoice.next') },
        { value: 2, label: this.$t('invoice.history') }
      ]
    },
    companyPrefixByProgramId() {
      return this.$store.state.invoice.mainPrograms.reduce(
        (prefixes, program) => {
          prefixes[String(program.acp_id)] = program.prefix
          return prefixes
        },
        {}
      )
    },
    apcList() {
      const companyIds = this.companyId.map(id => String(id))

      return this.$store.state.invoice.mainPrograms
        .filter(acp =>
          companyIds.length ? companyIds.includes(String(acp.company_id)) : true
        )
        .map(el => ({
          value: el.acp_id,
          label: `${el.prefix ? el.prefix + ' - ' : ''}${el.acp_name_foreign}`
        }))
    }
  },
  methods: {
    ...mapActions('invoice', [
      'getMainProgram',
      'fetchSubProgram',
      'fetchClassOperation',
      'fetchAcademicYears',
      'fetchAcademicTerms'
    ]),
    filterSelectOption(input, option) {
      const componentOptions = option.componentOptions || {}
      const propsData = componentOptions.propsData || {}
      const children = componentOptions.children || []
      const label = propsData.label || option.label || (children[0] && children[0].text)

      return String(label || '')
        .toLowerCase()
        .indexOf(String(input).toLowerCase()) >= 0
    },
    closeDropdowns() {
      this.mainProgramOpen = false
      this.subProgramOpen = false
      this.academicStatusOpen = false
      this.academicYearOpen = false
      this.academicTermOpen = false
      this.classOperationOpen = false
    },
    onMainDropdownVisibleChange(visible) {
      this.mainProgramOpen = visible
    },
    onSubProgramDropdownVisibleChange(visible) {
      this.subProgramOpen = visible && this.hasMainProgram
    },
    onAcademicStatusDropdownVisibleChange(visible) {
      this.academicStatusOpen = visible
    },
    onAcademicYearDropdownVisibleChange(visible) {
      this.academicYearOpen = visible
    },
    onAcademicTermDropdownVisibleChange(visible) {
      this.academicTermOpen =
        visible && this.hasBranch && this.hasMainProgram && this.hasAcademicYear
    },
    onClassOperationDropdownVisibleChange(visible) {
      this.classOperationOpen =
        visible && this.hasBranch && this.hasMainProgram && this.hasAcademicYear
    },
    onClassOperationChange() {
      this.classOperationOpen = false
    },
    classOperationLabel(classOperation) {
      const companyPrefix = this.companyPrefixByProgramId[
        String(classOperation.acp_id)
      ]
      const className =
        this.hasMultipleCompanies && companyPrefix
          ? companyPrefix + ' - ' + classOperation.class_name
          : classOperation.class_name
      const status = this.academicStatusLabel(classOperation.ac_status_id)

      return status ? className + ' (' + status + ')' : className
    },
    academicStatusLabel(statusId) {
      const keyByStatus = {
        1: 'current',
        2: 'history',
        3: 'next'
      }

      const statusKey = keyByStatus[Number(statusId)]

      return statusKey ? this.$t('invoice.' + statusKey) : ''
    },
    academicOptionLabel(option, name) {
      const status = this.academicStatusLabel(option.ac_status_id)
      const label = status ? name + ' (' + status + ')' : name
      const branchName = option.branch ? option.branch.branch_name_en : null

      return branchName ? label + ' - ' + branchName : label
    },
    onAcademicStatusChange() {
      this.academicStatusOpen = false
    },
    reloadAcademicStatusCascade() {
      this.closeDropdowns()
      this.academicYearId = []
      this.academicTermId = []
      this.classId = []
      this.academicYearOptions = []
      this.academicTermOptions = []
      this.classOperationOptions = []
      this.academicTermRequestId += 1
      this.classOperationRequestId += 1
      this.academicTermLoading = false
      this.classLoading = false
      this.loadAcademicYears()
    },
    onMainChange() {
      this.closeDropdowns()
      const requestId = ++this.subProgramRequestId

      this.apcsId = []
      this.academicTermId = []
      this.classId = []
      this.subProgramOptions = []
      this.academicTermOptions = []
      this.classOperationOptions = []
      this.academicTermRequestId += 1
      this.academicTermLoading = false
      this.loadClassOperations()

      if (!this.hasMainProgram) {
        this.subProgramLoading = false
        return
      }

      this.subProgramLoading = true
      this.fetchSubProgram({ acp_id: this.apcId })
        .then(response => {
          if (requestId !== this.subProgramRequestId) {
            return
          }

          this.subProgramOptions = response.data.map(el => ({
            value: el.acps_id,
            label: (el.prefix ? el.prefix + ' - ' : '') + el.acps_name_foreign
          }))
          this.loadAcademicTerms()
        })
        .catch(error => {
          if (requestId === this.subProgramRequestId) {
            this.$toasted.error(error.message)
          }
        })
        .finally(() => {
          if (requestId === this.subProgramRequestId) {
            this.subProgramLoading = false
          }
        })
    },
    onSubProgramChange() {
      this.subProgramOpen = false
      this.academicTermOpen = false
      this.classOperationOpen = false
      this.academicTermId = []
      this.classId = []
      this.academicTermOptions = []
      this.classOperationOptions = []
      this.loadAcademicTerms()
      this.loadClassOperations()
    },
    onAcademicYearChange() {
      this.academicYearOpen = false
      this.academicTermOpen = false
      this.classOperationOpen = false
      this.academicTermId = []
      this.classId = []
      this.academicTermOptions = []
      this.classOperationOptions = []
      this.loadAcademicTerms()
      this.loadClassOperations()
    },
    onAcademicTermChange() {
      this.academicTermOpen = false
      this.classOperationOpen = false
      this.classId = []
      this.classOperationOptions = []
      this.loadClassOperations()
    },
    loadAcademicYears() {
      const requestId = ++this.academicYearRequestId

      this.academicYearLoading = false
      this.academicYearOptions = []

      this.academicYearLoading = true
      this.fetchAcademicYears({
        branch_id: this.effectiveBranchIds,
        ac_status_id: this.academicStatusId,
        all_branch: !this.effectiveBranchIds.length
      })
        .then(response => {
          if (requestId !== this.academicYearRequestId) {
            return
          }

          this.academicYearOptions = response.data.map(el => ({
            value: el.ac_year_id,
            label: this.academicOptionLabel(el, el.ac_year)
          }))
        })
        .catch(error => {
          if (requestId === this.academicYearRequestId) {
            this.$toasted.error(error.message)
          }
        })
        .finally(() => {
          if (requestId === this.academicYearRequestId) {
            this.academicYearLoading = false
          }
        })
    },
    loadAcademicTerms() {
      const requestId = ++this.academicTermRequestId

      this.academicTermLoading = false
      this.academicTermOptions = []
      if (!this.hasBranch || !this.hasMainProgram || !this.hasAcademicYear) {
        return
      }

      const subProgramIds = this.apcsId.length
        ? this.apcsId
        : this.subProgramOptions.map(option => option.value)
      if (!subProgramIds.length) {
        return
      }

      const filters = {
        branch_id: this.effectiveBranchIds,
        ac_year_id: this.academicYearId,
        ac_status_id: this.academicStatusId
      }
      filters.acps_id = subProgramIds

      this.academicTermLoading = true
      this.fetchAcademicTerms(filters)
        .then(response => {
          if (requestId !== this.academicTermRequestId) {
            return
          }

          this.academicTermOptions = response.data.map(el => ({
            value: el.ac_term_id,
            label: this.academicOptionLabel(el, el.term_name)
          }))
        })
        .catch(error => {
          if (requestId === this.academicTermRequestId) {
            this.$toasted.error(error.message)
          }
        })
        .finally(() => {
          if (requestId === this.academicTermRequestId) {
            this.academicTermLoading = false
          }
        })
    },
    loadClassOperations() {
      const requestId = ++this.classOperationRequestId

      this.classLoading = false
      if (!this.hasBranch || !this.hasMainProgram || !this.hasAcademicYear) {
        return
      }

      const filters = {
        branch_id: this.effectiveBranchIds,
        acp_id: this.apcId,
        ac_year_id: this.academicYearId,
        ac_status_id: this.academicStatusId
      }
      if (this.apcsId.length) {
        filters.acps_id = this.apcsId
      }
      if (this.academicTermId.length) {
        filters.ac_term_id = this.academicTermId
      }

      this.classLoading = true
      this.fetchClassOperation(filters)
        .then(response => {
          if (requestId !== this.classOperationRequestId) {
            return
          }

          this.classOperationOptions = response.data.map(el => ({
            value: el.class_id,
            label: this.classOperationLabel(el)
          }))
        })
        .catch(error => {
          if (requestId === this.classOperationRequestId) {
            this.$toasted.error(error.message)
          }
        })
        .finally(() => {
          if (requestId === this.classOperationRequestId) {
            this.classLoading = false
          }
        })
    }
  },
  watch: {
    academicStatusId() {
      this.reloadAcademicStatusCascade()
    },
    effectiveBranchIds() {
      this.closeDropdowns()
      this.academicYearId = []
      this.academicTermId = []
      this.classId = []
      this.academicYearOptions = []
      this.academicTermOptions = []
      this.classOperationOptions = []
      this.academicTermRequestId += 1
      this.classOperationRequestId += 1
      this.academicTermLoading = false
      this.classLoading = false

      const validProgramIds = this.apcList.map(program => program.value)
      const selectedProgramIds = this.apcId.filter(id =>
        validProgramIds.includes(id)
      )
      if (selectedProgramIds.length !== this.apcId.length) {
        this.subProgramRequestId += 1
        this.subProgramLoading = false
        this.apcId = selectedProgramIds
        this.apcsId = []
        this.subProgramOptions = []
      }

      this.loadAcademicYears()
    }
  }
}
</script>

<style>
.invoice-filter-drawer .ant-drawer-content {
  position: relative;
}

.invoice-filter-drawer .ant-drawer-content-wrapper {
  max-width: 100vw;
}

.invoice-filter-drawer .ant-drawer-body {
  height: calc(100% - 55px);
  padding: 0;
  overflow: hidden;
}

.invoice-filter-drawer .ant-drawer-body > .loading-banner-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 18px 20px 0;
  overflow-y: auto;
}

.invoice-filter-section {
  flex: 0 0 auto;
  padding: 18px 0;
}

.invoice-filter-section--first {
  padding-top: 0;
}

.invoice-filter-section__title {
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  text-transform: uppercase;
}

.invoice-filter-section__title::after {
  flex: 1;
  border-top: 1px solid #d9e0e7;
  content: '';
}

.invoice-filter-program-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
  margin-top: 16px;
}

.invoice-filter-section--invoice {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
}

.invoice-filter-section--invoice > .invoice-filter-section__title {
  grid-column: 1 / -1;
  margin-bottom: 0;
}

.invoice-filter-section--invoice > .invoice-filter-field:last-child {
  grid-column: 1 / -1;
}

.invoice-filter-section > .invoice-filter-field + .invoice-filter-field {
  margin-top: 16px;
}

.invoice-filter-section--invoice
  > .invoice-filter-field
  + .invoice-filter-field {
  margin-top: 0;
}

.invoice-filter-field {
  min-width: 0;
}

.invoice-filter-label {
  display: block;
  margin: 0 0 7px;
  color: #1f2933;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.invoice-filter-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 7px 0 0;
  color: #4a6f98;
  font-size: 12px;
  line-height: 1.4;
}

.invoice-filter-loading .fa-circle-notch {
  color: #3490dc;
}

.invoice-filter-drawer .ant-select,
.invoice-filter-drawer .ant-calendar-picker {
  width: 100%;
}

.invoice-filter-drawer .ant-select-selection,
.invoice-filter-drawer .ant-calendar-picker-input {
  min-height: 36px;
  border-radius: 4px;
}

.invoice-filter-drawer .ant-select-selection--multiple {
  padding-bottom: 2px;
}

.invoice-filter-drawer
  .ant-select-selection--multiple
  .ant-select-selection__rendered {
  margin-bottom: 0;
}

.invoice-filter-status {
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #f8fafc;
}

.invoice-filter-status .checkbox {
  margin: 0;
}

.invoice-filter-status .checkbox label {
  margin: 0;
  color: #334155;
  font-weight: 600;
}

.invoice-filter-status__hint {
  margin: 6px 0 0 26px;
  color: #718096;
  font-size: 12px;
  line-height: 1.45;
}

.invoice-filter-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex: 0 0 auto;
  margin: auto -20px 0;
  padding: 12px 20px;
  border-top: 1px solid #d9e0e7;
  background: #fff;
  box-shadow: 0 -4px 12px rgba(31, 41, 51, 0.06);
}

.invoice-filter-actions .btn {
  width: auto;
  min-width: 120px;
  min-height: 38px;
}

.invoice-filter-actions .btn:last-child {
  min-width: 160px;
}

@media (max-width: 640px) {
  .invoice-filter-program-fields,
  .invoice-filter-section--invoice {
    grid-template-columns: minmax(0, 1fr);
  }

  .invoice-filter-section--invoice > .invoice-filter-field:last-child {
    grid-column: auto;
  }

  .invoice-filter-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invoice-filter-actions .btn,
  .invoice-filter-actions .btn:last-child {
    width: 100%;
    min-width: 0;
  }
}
</style>
